export type AvailabilityRestrictionPeriod = {
  label: string;
  startAt: string;
  endAt: string;
  type?: string;
};

const ALLOWED_RESTRICTION_TYPES = new Set([
  "full_day",
  "day_period",
  "time_range",
]);

function inferAvailabilityRestrictionType(period: AvailabilityRestrictionPeriod) {
  const explicitType = String(period.type || "").trim().toLowerCase();
  if (ALLOWED_RESTRICTION_TYPES.has(explicitType)) return explicitType;
  const label = String(period.label || "").toLowerCase();
  if (label.includes(" das ") && label.includes(" às ")) return "time_range";
  const startDate = new Date(period.startAt);
  const endDate = new Date(period.endAt);
  if (
    !Number.isNaN(startDate.getTime()) &&
    !Number.isNaN(endDate.getTime()) &&
    startDate.getHours() === 0 &&
    startDate.getMinutes() === 0 &&
    endDate.getHours() === 23 &&
    endDate.getMinutes() >= 58
  ) {
    return "full_day";
  }
  return "day_period";
}

export function formatAvailabilityPeriodLines(
  periods?: AvailabilityRestrictionPeriod[] | null,
) {
  return (Array.isArray(periods) ? periods : [])
    .map((period) => {
      const type = inferAvailabilityRestrictionType(period);
      const items = [period.label, period.startAt, period.endAt];
      if (ALLOWED_RESTRICTION_TYPES.has(type)) items.push(type);
      return items
        .map((item) => String(item || "").trim())
        .filter(Boolean)
        .join(" | ");
    })
    .filter(Boolean)
    .join("\n");
}

export function parseAvailabilityPeriodLines(value: string) {
  return String(value || "")
    .split(/\r?\n/g)
    .map((line) => {
      const [label, startAt, endAt, type] = line
        .split("|")
        .map((item) => item.trim());
      if (!label || !startAt || !endAt) return null;
      const normalizedType = String(type || "").trim().toLowerCase();
      const safeType = ALLOWED_RESTRICTION_TYPES.has(normalizedType)
        ? normalizedType
        : "";
      return {
        label,
        startAt,
        endAt,
        ...(safeType ? { type: safeType } : {}),
      };
    })
    .filter((item): item is AvailabilityRestrictionPeriod => Boolean(item));
}

export function normalizeAvailabilityRestrictionTypes(value?: string[]) {
  const normalized = (Array.isArray(value) ? value : [])
    .map((item) => String(item || "").trim().toLowerCase())
    .filter((item) => ALLOWED_RESTRICTION_TYPES.has(item));
  return Array.from(new Set(normalized));
}

export function availabilityRestrictionTypesFromPeriods(
  periods?: AvailabilityRestrictionPeriod[] | null,
) {
  const normalized = (Array.isArray(periods) ? periods : [])
    .map((period) => inferAvailabilityRestrictionType(period))
    .filter((item) => ALLOWED_RESTRICTION_TYPES.has(item));
  return Array.from(new Set(normalized));
}

export function availabilityRestrictionTypeLabel(value: string) {
  if (value === "full_day") return "Dia inteiro";
  if (value === "day_period") return "Período do dia";
  if (value === "time_range") return "Intervalo específico";
  return value;
}
